<template>
  <div
    class="flex h-[calc(100dvh-70px)] flex-col gap-12 overflow-auto px-5 py-3"
  >
    <div
      :class="{ grow: whitelistedUrl || !deviceInfo.isMiniProgram }"
      class="flex flex-col gap-5"
    >
      <div
        :class="[whitelistedUrl ? 'bg-[#FF7F00]' : 'bg-[#F22A42]']"
        class="flex min-h-[180px] justify-start rounded-[10px] px-3"
      >
        <div class="relative mr-1 w-32 flex-none shrink-0">
          <img
            :src="image"
            alt=""
            class="absolute bottom-0 left-0 h-auto max-h-36 w-full object-contain"
          />
        </div>
        <div class="flex min-w-0 flex-1 flex-col justify-center text-white">
          <h2 class="mr-3 ml-2 text-[22px] font-bold hyphens-auto break-words">
            {{
              blacklistedUrl
                ? $t('third_party_code.counterfeit')
                : $t('third_party_code.not_secure_code')
            }}
          </h2>
        </div>
      </div>
      <div
        v-if="!blacklistedUrl"
        class="flex items-center justify-center gap-2.5 rounded-[10px] bg-[#FAFAFA] px-3 py-1.5"
      >
        <p class="text-lg">
          {{ $t('third_party_code.authenticate_security_pattern') }}
        </p>
        <video
          ref="videoElement"
          :src="videoInstruction"
          autoplay
          class="h-[168px] w-full max-w-28"
          loop
          muted
          playsinline
        ></video>
      </div>
    </div>
    <div class="flex grow-0 flex-col items-center gap-4 text-center">
      <div
        v-if="showReportContainer"
        class="flex flex-col items-center gap-1.5"
      >
        <div class="text-[#F22A42]">
          <p v-if="!blacklistedUrl" class="text-lg font-bold">
            {{ $t('third_party_code.suspect') }}
          </p>
          <p>{{ $t('third_party_code.report_to_us') }}</p>
        </div>
        <ButtonComponent
          v-if="!deviceInfo.isMiniProgram"
          :buttonClass="reportButtonClass"
          :isLoading="isLoading"
          :onClick="handleReport"
          buttonName="result.qr_not_st.report"
        />
      </div>
      <div class="text-center mb-3">
        <ButtonComponent
          :onClick="handleRescan"
          buttonClass="underline text-lg text-[#5D5D5D] font-normal"
          buttonName="result.qr_not_st.scan_another_code"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { events } from '@/utils/events'
import reportThirdPartyContent from '@/utils/thirdPartyContentTrackingAPI.js'
import { getAppName } from '@/utils/getAppName.js'
import getDeviceHeader from '@/utils/getDeviceHeader.js'
import getCountryName from '@/utils/getCountryName.js'
import * as amplitude from '@amplitude/analytics-browser'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { fetchLocationFromApi } from '@/utils/getLocation.js'
import * as Sentry from '@sentry/vue'
import { onWeChatReady } from '@/utils/useWeChatSDK.js'

import noSgImage from '@/assets/images/3rd_party_code_no_sg_image.png'
import noStImage from '@/assets/images/3rd_party_code_no_st_image.png'
import videoInstruction from '@/assets/qr_3rd_party.mp4'
import ButtonComponent from '../ButtonComponent.vue'

const { locale } = useI18n()
const emits = defineEmits(['reopenCamera'])
const props = defineProps({
  installId: String,
  decodedData: Object,
  deviceInfo: Object
})

const location = ref({})
const currentLanguage = ref(locale.value)
const isLoading = ref(false)
const qrUrl = ref('')
const unsupportedScanData = ref(null)
const app_name = ref('')
const videoElement = ref(null)
const decodedData = props.decodedData
const userId = ref('')
const whitelistedUrl = ref(false)
const blacklistedUrl = ref(false)
const deviceHeader = ref('')

const reportButtonClass = computed(() =>
  [
    'text-white',
    'text-lg',
    'font-bold',
    'p-2.5',
    'min-w-[88px]',
    'max-h-[41px]',
    isLoading.value ? 'bg-[#F22A42]/50' : 'bg-[#F22A42]'
  ].join(' ')
)
const image = computed(() => (whitelistedUrl.value ? noSgImage : noStImage))

const showReportContainer = computed(
  () => !whitelistedUrl.value && !props.deviceInfo.isMiniProgram
)

const thirdPartyDetails = computed(
  () => events.value.third_party_content_details || null
)

const loadThirdPartyContentRules = (details) => {
  if (!details) return

  const isPositiveMatch = (check) => {
    if (!check) return false

    const typeMatch = typeof check.type === 'number' && check.type > 0
    const companyMatch =
      typeof check.company_id === 'number' && check.company_id > 0
    return typeMatch || companyMatch
  }

  const wl = isPositiveMatch(details.whitelist_check)
  const bl = isPositiveMatch(details.blacklist_check)

  const validUrl = details.is_valid_url

  whitelistedUrl.value = !validUrl || wl
  blacklistedUrl.value = bl
}

const handleLocationData = async () => {
  location.value = events.value.locationData

  if (!(location.value.latitude && location.value.longitude)) {
    location.value = await fetchLocationFromApi()
    location.value.positioning = 2
  } else {
    try {
      location.value.country = await getCountryName(
        location.value.latitude,
        location.value.longitude
      )
    } catch (error) {
      console.error('Error fetching country name:', error)
      location.value.country = ''
    }
    location.value.positioning = 4
  }
}

const playVideo = async () => {
  await nextTick()
  if (videoElement.value) {
    videoElement.value.play().catch((error) => {
      console.error('Example Video failed to play:', error.message)
    })
  }
}

onMounted(async () => {
  isLoading.value = true

  app_name.value = await getAppName()

  if (events.value.carrier_content) {
    qrUrl.value = events.value.carrier_content
  }
  if (thirdPartyDetails.value) {
    loadThirdPartyContentRules(thirdPartyDetails.value)
  }

  // Track NON_ST_CODE event with the URL that triggered this third-party result
  amplitude.track('NON_ST_CODE', { url: qrUrl.value })

  try {
    const type = blacklistedUrl.value
      ? 'blacklist'
      : whitelistedUrl.value
        ? 'whitelist'
        : 'unknown'
    userId.value = decodedData.isDataFromMobileApp
      ? decodedData.userid
      : props.installId

    await handleLocationData()

    deviceHeader.value = await getDeviceHeader(decodedData, props.deviceInfo)

    if (!unsupportedScanData.value) {
      unsupportedScanData.value = await reportThirdPartyContent(
        qrUrl.value,
        userId.value,
        location.value,
        type,
        {
          appSlug: app_name.value,
          deviceHeader: deviceHeader.value
        }
      )
    }

    isLoading.value = false
  } catch (error) {
    isLoading.value = false
    Sentry.captureException(new Error(error))
  }

  onWeChatReady(playVideo)
})

watch(
  () => locale.value,
  (newLang) => {
    currentLanguage.value = newLang
  }
)

const handleReport = () => {
  amplitude.track('Report code continue', { ...decodedData })

  const formId = import.meta.env.VITE_FAKE_URL_REPORT_FORM_ID
  const version = __APP_VERSION__

  window.location.href = `https://scantrust.typeform.com/to/${formId}#lang=${currentLanguage.value}&userid=${userId.value}&device=${props.deviceInfo.model}&app_name=${app_name.value}&app_version=${version}&url=${qrUrl.value || ''}&scanid=${unsupportedScanData.value.uuid}&latitude=${location.value.latitude}&longitude=${location.value.longitude}&country=${location.value.country}&source=${location.value.source}`
}

const handleRescan = () => {
  emits('reopenCamera')
}
</script>
